import { Link, useNavigate, useParams } from 'react-router-dom';

import {
  useExperiences,
  experienceEmoji,
  EXPERIENCE_CATEGORY_LABELS,
  type ExperienceCategory,
} from '@/hooks/useExperiences';
import { Badge, Button, Card, CardTitle, toast } from '@/components/ui';
import { formatCOP } from '@/lib/format';
import { useCart } from '@/store/cart';

/**
 * ExperienceDetail — ROUND7 P3 / ROUND8. Detalle de una experiencia VIP +
 * booking CTA navegando a `/checkout?mode=experience&id=<id>`. Datos reales
 * desde `public.experiences` vía `useExperiences()`.
 */
export function ExperienceDetailPage(): JSX.Element {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: experiences, isLoading, isError } = useExperiences();
  const addProductLine = useCart((s) => s.addProductLine);

  if (isLoading) {
    return (
      <section className="container-fc py-12" aria-busy="true">
        <div className="h-8 w-64 rounded bg-neutral-100" />
        <div className="mt-4 h-5 w-full max-w-md rounded bg-neutral-100" />
        <div className="mt-8 h-40 w-full rounded-card bg-neutral-100" />
      </section>
    );
  }

  const exp = experiences?.find((e) => e.id === id) ?? null;

  if (isError || !exp) {
    return (
      <section className="container-fc py-12">
        <h1 className="font-display text-3xl">
          {isError ? 'No pudimos cargar la experiencia' : 'Experiencia no encontrada'}
        </h1>
        <p className="mt-2 text-fc-muted">
          {isError
            ? 'Ocurrió un error al cargar esta experiencia. Probá recargar la página en unos minutos.'
            : 'La experiencia que buscás no existe. Volvé al listado completo.'}
        </p>
        <Button className="mt-6" variant="secondary" onClick={() => navigate('/experiences')}>
          Ver experiencias
        </Button>
      </section>
    );
  }

  const categoryLabel =
    EXPERIENCE_CATEGORY_LABELS[exp.category as ExperienceCategory] ?? exp.category;

  function handleBook(): void {
    if (!exp) return;
    navigate(`/checkout?mode=experience&id=${exp.id}`);
  }

  /** ROUND19 #4: agregar la experiencia al carrito como productLine (mismo
   * patrón que partners). El productId queda namespaced (`experience:<id>`)
   * para no chocar con partner_products. */
  function handleAddToCart(): void {
    if (!exp) return;
    addProductLine({
      productId: `experience:${exp.id}`,
      partnerId: 'first-class-experiences',
      partnerName: 'Experiencias First Class',
      title: exp.title,
      unitPriceCents: exp.priceCents,
      quantity: 1,
    });
    toast.success({ title: 'Agregada al carrito', description: exp.title });
  }

  return (
    <section className="container-fc py-8 md:py-12">
      <nav aria-label="Breadcrumb" className="mb-4 text-sm text-fc-muted">
        <Link to="/experiences" className="hover:text-fc-ink">
          Experiencias
        </Link>
        <span aria-hidden> / </span>
        <span className="text-fc-ink">{exp.title}</span>
      </nav>

      <div className="grid grid-cols-1 gap-8 md:grid-cols-2">
        <div
          role="img"
          aria-label={`Hero de ${exp.title}`}
          className="flex aspect-[4/3] items-center justify-center rounded-card border border-neutral-200 bg-neutral-50"
        >
          <span aria-hidden className="text-7xl">
            {experienceEmoji(exp.category)}
          </span>
        </div>

        <div>
          <Badge tone="gold" className="mb-3">
            {categoryLabel}
          </Badge>
          <h1 className="font-display text-3xl md:text-4xl">{exp.title}</h1>
          {exp.subtitle && <p className="mt-1 text-sm text-fc-muted">{exp.subtitle}</p>}

          <dl className="mt-6 grid grid-cols-2 gap-y-2 text-sm">
            {exp.location && (
              <>
                <dt className="text-fc-muted">Lugar</dt>
                <dd className="text-fc-ink">{exp.location}</dd>
              </>
            )}
            {exp.duration && (
              <>
                <dt className="text-fc-muted">Duración</dt>
                <dd className="text-fc-ink">{exp.duration}</dd>
              </>
            )}
            {exp.maxCapacity != null && (
              <>
                <dt className="text-fc-muted">Capacidad</dt>
                <dd className="text-fc-ink">{exp.maxCapacity} personas</dd>
              </>
            )}
            <dt className="text-fc-muted">Precio</dt>
            <dd className="font-semibold tabular-nums text-fc-ink">{formatCOP(exp.priceCents)}</dd>
          </dl>

          {/* ROUND19 #4: CTA primaria pasa a "Agregar al carrito" (flow
              unificado en /cart). "Reservar experiencia" queda como CTA
              secundaria para el flow legacy `?mode=experience`. */}
          <div className="mt-6 flex flex-col gap-2 sm:flex-row">
            <Button variant="primary" size="lg" onClick={handleAddToCart}>
              Agregar al carrito
            </Button>
            <Button variant="secondary" size="lg" onClick={handleBook}>
              Reservar experiencia
            </Button>
          </div>
        </div>
      </div>

      {exp.description && (
        <Card variant="default" padded className="mt-8">
          <CardTitle>La experiencia</CardTitle>
          <p className="mt-3 text-fc-ink">{exp.description}</p>
        </Card>
      )}

      {exp.highlights.length > 0 && (
        <Card variant="ghost" padded className="mt-6">
          <CardTitle>Lo que destaca</CardTitle>
          <ul className="mt-4 flex flex-col gap-2 text-sm">
            {exp.highlights.map((h, i) => (
              <li key={i} className="flex items-start gap-2">
                <span
                  aria-hidden
                  className="mt-1.5 inline-block h-1.5 w-1.5 flex-none rounded-full bg-accent"
                />
                <span>{h}</span>
              </li>
            ))}
          </ul>
        </Card>
      )}
    </section>
  );
}
